// Default per-week ROM goals for the TracPatch Progress Test, used to seed the
// admin override editor's cells when a patient has no stored override for a
// (week, field). This is a COPY of PROGRESS_WEEK_GOALS in
// physio_app_web/components/TracPatch/ProgressTestPage.js — the patient app and
// the admin don't share code (same duplication pattern as deriveBands() in
// tracpatch.config.modal.js). KEEP THE TWO TABLES IN SYNC: edit both or neither.
//
// Values are DISPLAYED / clinical degrees — what the patient reads on the dial:
//   0° = full extension (straight), 90° = seated rest, higher = deeper flexion.
// week = -1 is the Pre-Op row (tightest window); each week widens the window.
// The full recovery goal is a 0-135 knee ROM: full extension (0) and 135 of
// flexion. The weekly rows ramp from the tight post-op window up to that full
// 0/135 goal by week 12 (clamped after).
export const DEFAULT_PROGRESS_WEEK_GOALS = {
  '-1': { extension: 35, flexion:  90 },
  '0':  { extension: 30, flexion:  90 },
  '1':  { extension: 25, flexion:  95 },
  '2':  { extension: 20, flexion: 100 },
  '3':  { extension: 16, flexion: 105 },
  '4':  { extension: 12, flexion: 110 },
  '5':  { extension:  9, flexion: 115 },
  '6':  { extension:  7, flexion: 119 },
  '7':  { extension:  5, flexion: 122 },
  '8':  { extension:  4, flexion: 125 },
  '9':  { extension:  3, flexion: 128 },
  '10': { extension:  2, flexion: 130 },
  '11': { extension:  1, flexion: 133 },
  '12': { extension:  0, flexion: 135 },
  '13': { extension:  0, flexion: 135 },
}

// Last week with a configured default row. Weeks past this clamp to it.
export const PROGRESS_GOAL_MAX_WEEK = 13
// Rows shown in the editor: Pre-Op (-1) then Weeks 0..PROGRESS_GOAL_MAX_WEEK.
export const PROGRESS_GOAL_WEEKS = [
  -1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13,
]
export const PROGRESS_GOAL_FIELDS = ['extension', 'flexion']

// Resolve the default goal row for a week, clamping out-of-range weeks.
export function defaultGoalForWeek(week) {
  let w
  if (week < 0) w = -1
  else if (week > PROGRESS_GOAL_MAX_WEEK) w = PROGRESS_GOAL_MAX_WEEK
  else w = week
  return DEFAULT_PROGRESS_WEEK_GOALS[String(w)] || DEFAULT_PROGRESS_WEEK_GOALS['0']
}

// The default value for a single (week, field) cell.
export function defaultGoalCell(week, field) {
  const row = defaultGoalForWeek(week)
  return row ? row[field] : undefined
}

// The EFFECTIVE base value for a (week, field) cell — i.e. what a patient
// inherits when they have no per-patient override. A clinic-level override
// (array of { week, extension, flexion }, set on the clinic settings page)
// wins over the hardcoded MoveAI default table. `clinicOverrides` may be
// undefined/empty, in which case this is just the hardcoded default — keeping
// every existing caller (which passes no clinic layer) unchanged.
export function effectiveDefaultCell(week, field, clinicOverrides) {
  const list = Array.isArray(clinicOverrides) ? clinicOverrides : []
  const entry = list.find((r) => r && r.week === week)
  if (entry && entry[field] !== undefined && entry[field] !== null) {
    return entry[field]
  }
  return defaultGoalCell(week, field)
}

// Resolve a (week, field) cell. Precedence: per-patient `overrides` →
// clinic-level `clinicOverrides` → hardcoded default table. Both arrays are
// lists of { week, extension, flexion } and may be undefined/empty.
export function resolveGoalCell(week, field, overrides, clinicOverrides) {
  const list = Array.isArray(overrides) ? overrides : []
  const entry = list.find((r) => r && r.week === week)
  if (entry && entry[field] !== undefined && entry[field] !== null) {
    return entry[field]
  }
  return effectiveDefaultCell(week, field, clinicOverrides)
}

// True when the override list stores a value for (week, field) that differs
// from the EFFECTIVE base (clinic override if any, else the hardcoded default)
// — used to render the "overridden" blue dot/border.
export function isGoalCellOverridden(week, field, overrides, clinicOverrides) {
  const list = Array.isArray(overrides) ? overrides : []
  const entry = list.find((r) => r && r.week === week)
  return !!(
    entry &&
    entry[field] !== undefined &&
    entry[field] !== null &&
    entry[field] !== effectiveDefaultCell(week, field, clinicOverrides)
  )
}

// Immutably set a (week, field) cell on the override list. Clearing the value
// (empty/null) or matching the EFFECTIVE base (clinic override if any, else the
// hardcoded default) drops the field so the cell falls back to that base; a row
// with no remaining fields is removed entirely. Returns the next override array
// (sorted by week). `clinicOverrides` is optional — omitting it compares against
// the hardcoded default, preserving the prior single-layer behavior.
export function setGoalCell(overrides, week, field, value, clinicOverrides) {
  const list = Array.isArray(overrides) ? overrides : []
  const existing = list.find((r) => r && r.week === week)
  const draft = { ...(existing || { week }) }
  if (
    value === undefined ||
    value === null ||
    value === '' ||
    value === effectiveDefaultCell(week, field, clinicOverrides)
  ) {
    delete draft[field]
  } else {
    draft[field] = value
  }
  const hasAny = PROGRESS_GOAL_FIELDS.some(
    (f) => draft[f] !== undefined && draft[f] !== null
  )
  const others = list.filter((r) => r && r.week !== week)
  return hasAny
    ? [...others, draft].sort((a, b) => a.week - b.week)
    : others
}
